import Accordion from "@material-ui/core/Accordion";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import blue from "@material-ui/core/colors/blue";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import AccessibilityNew from "@material-ui/icons/AccessibilityNew";
import Assignment from "@material-ui/icons/Assignment";
import Comment from "@material-ui/icons/Comment";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import clsx from "clsx";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ResponsiveContainer } from "recharts";
import { useDebouncedCallback } from "use-debounce";

import { Dispatch, StoreState } from "../../store";
import { isFeatureEnabled, numberFormat } from "../../utilities";
import CircularProgressbar from "../CircularProgressbar";
import PieChart from "../generic/PieChart";
import ProgressOverlay from "../ProgressOverlay";
import {
  createChurnHistogram,
  createClvHistogram,
  createNextBestActivityBarChart,
} from "./CustomerGroupResultCharts";
import ElectricityIcon from "./icons/electricity.png";
import GasIcon from "./icons/gas.png";
import GlassFiberIcon from "./icons/glassfiber.png";
import HeatingIcon from "./icons/heating.png";

const COLORS = [blue[100], blue[300], blue[500], blue[700], blue[900]];

const useStyles = makeStyles((theme) => ({
  button: {
    margin: theme.spacing(1),
  },
  container: {
    alignItems: "center",
    margin: "5px",
  },
  icon: {
    color: "white",
    fontSize: "1.5rem",
    height: "50px",
    padding: "0px",
  },
  pie: {
    fontFamily: "Arial, sans-serif",
  },
  progressbarWrapper: {
    height: "280px",
    margin: "auto",
  },
  tile: {
    backgroundColor: "#002E5F",
    color: "white",
    fontFamily: "Arial, sans-serif",
    fontSize: "1rem",
    margin: "5px",
    minWidth: "160px",
    overflow: "inherit",
    padding: "10px !important",
    pointerEvents: "none",
  },
  contact: {
    fontSize: "1rem",
  },
}));

const CustomerGroupResult: React.FC = () => {
  const classes = useStyles();
  const [clvChurnAccordionExpanded, setClvChurnAccordionExpanded] = useState(
    true
  );
  const [
    nextBestActivityAccordionExpanded,
    setNextBestActivityAccordionExpanded,
  ] = useState(true);
  const [
    optInAndServiceAccordionExpanded,
    setOptInAndServiceAccordionExpanded,
  ] = useState(true);
  const [tileAccordionExpanded, setTileAccordionExpanded] = useState(true);
  const [triggerBuildAgeGroupArray, setTriggerBuildAgeGroupArray] = useState(
    false
  );
  const [ageGroupArray, setAgeGroupArray] = useState<
    Array<{
      value: number;
      label: string;
    }>
  >([]);
  // eslint-disable-next-line no-unused-vars
  const [initialFetch, setInitialFetch] = useState(true);

  const dispatch = useDispatch<Dispatch>();

  const customerGroupFilter = useSelector(
    (state: StoreState) => state.customerGroupFilter
  );

  const customerGroupResult = useSelector(
    (state: StoreState) => state.customerGroupResult
  );

  const buildAgeGroupArray = useCallback((): Array<{
    value: number;
    label: string;
  }> => {
    let ageGroups = [
      {
        value:
          (customerGroupResult.meta.ageYoungestCount * 100) /
          (customerGroupResult.meta.customerCount || 1),
        label:
          customerGroupResult.meta.ageYoungestCount > 0 ? "unter 26 Jahre" : "",
      },
      {
        value:
          (customerGroupResult.meta.ageYoungCount * 100) /
          (customerGroupResult.meta.customerCount || 1),
        label:
          customerGroupResult.meta.ageYoungCount > 0 ? "26 - 35 Jahre" : "",
      },
      {
        value:
          (customerGroupResult.meta.ageMediumCount * 100) /
          (customerGroupResult.meta.customerCount || 1),
        label:
          customerGroupResult.meta.ageMediumCount > 0 ? "36 - 50 Jahre" : "",
      },
      {
        value:
          (customerGroupResult.meta.ageOldCount * 100) /
          (customerGroupResult.meta.customerCount || 1),
        label: customerGroupResult.meta.ageOldCount > 0 ? "51 - 65 Jahre" : "",
      },
      {
        value:
          (customerGroupResult.meta.ageOldestCount * 100) /
          (customerGroupResult.meta.customerCount || 1),
        label:
          customerGroupResult.meta.ageOldestCount > 0 ? "über 65 Jahre" : "",
      },
    ];

    ageGroups = ageGroups.filter((v) => v.value !== 0);

    return ageGroups;
  }, [customerGroupResult.meta]);

  const fetchDataDebounced = useDebouncedCallback(
    () =>
      dispatch.customerGroupResult.fetchCustomerGroup({ customerGroupFilter }),
    500
  );

  const fetchData = useCallback(
    () =>
      dispatch.customerGroupResult.fetchCustomerGroup({ customerGroupFilter }),
    [customerGroupFilter, dispatch.customerGroupResult]
  );

  useEffect(() => {
    setInitialFetch((isInitialFetch) => {
      if (isInitialFetch) {
        fetchData();
      } else {
        fetchDataDebounced();
      }
      return false;
    });
  }, [fetchData, fetchDataDebounced]);

  useEffect(() => {
    setAgeGroupArray([]);
    setTriggerBuildAgeGroupArray(true);
    createClvHistogram(customerGroupResult.meta);
    createChurnHistogram(customerGroupResult.meta);
    createNextBestActivityBarChart(customerGroupResult.meta);
  }, [buildAgeGroupArray, customerGroupResult]);

  useEffect(() => {
    if (triggerBuildAgeGroupArray) {
      setAgeGroupArray(buildAgeGroupArray());
      setTriggerBuildAgeGroupArray(false);
    }
  }, [buildAgeGroupArray, triggerBuildAgeGroupArray]);

  const getAmountOptIn = () =>
    (customerGroupResult.meta.optInCount * 100) /
    (customerGroupResult.meta.customerCount || 1);

  const getAmountBasicSupply = () =>
    (customerGroupResult.meta.basicSupplyCount * 100) /
    (customerGroupResult.meta.customerCount || 1);

  const ageHeadline = () => {
    let ageTypo;
    tileAccordionExpanded ? (ageTypo = "Anteil Altersgruppe") : (ageTypo = "");
    return ageTypo;
  };

  const handleClvChurnAccordionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    expanded: boolean
  ) => {
    setClvChurnAccordionExpanded(expanded);
  };

  const handleNextBestActivityAccordionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    expanded: boolean
  ) => {
    setNextBestActivityAccordionExpanded(expanded);
  };

  const handleOptInAndServiceAccordionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    expanded: boolean
  ) => {
    setOptInAndServiceAccordionExpanded(expanded);
  };

  const handleTileAccordionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    expanded: boolean
  ) => {
    setTileAccordionExpanded(expanded);
  };

  return (
    <ProgressOverlay loading={customerGroupResult.loading}>
      <Accordion
        expanded={tileAccordionExpanded}
        onChange={handleTileAccordionChange}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Grid item={true} xs={12} lg={6}>
            <Typography variant="h6" align="left">
              Kundeninformationen
            </Typography>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container={true}>
            <Grid item={true} xs={12} lg={6}>
              <ResponsiveContainer width="100%" height="100%">
                <Grid container={true}>
                  <Grid item={true} xs={12}>
                    <Grid
                      container={true}
                      spacing={2}
                      className={classes && classes.container}
                    >
                      <Grid
                        item={true}
                        xs={1}
                        className={classes && classes.tile}
                      >
                        <IconButton
                          aria-label={`info about `}
                          className={classes && classes.icon}
                        >
                          <AccessibilityNew /> &nbsp;
                          {numberFormat(
                            customerGroupResult.meta.customerCount,
                            false
                          )}
                        </IconButton>
                        <br />
                        <br />
                        <br />
                        Anzahl
                        <br />
                        Kunden
                      </Grid>
                      <Grid
                        item={true}
                        xs={1}
                        className={classes && classes.tile}
                      >
                        <IconButton
                          aria-label={`info about `}
                          className={classes && classes.icon}
                        >
                          <Assignment /> &nbsp;
                          {numberFormat(
                            customerGroupResult.meta.contractCount,
                            false
                          )}
                        </IconButton>
                        <br />
                        <br />
                        <br />
                        Anzahl
                        <br />
                        Verträge
                      </Grid>
                      <Grid
                        item={true}
                        xs={1}
                        className={classes && classes.tile}
                      >
                        <IconButton
                          aria-label={`info about `}
                          className={
                            classes && clsx([classes.icon, classes.contact])
                          }
                        >
                          <Comment /> &nbsp;
                          {customerGroupResult.meta.commonContact || "-"}
                        </IconButton>
                        <br />
                        <br />
                        <br />
                        Häufigster
                        <br />
                        Kontakt
                      </Grid>
                    </Grid>
                    <Grid
                      container={true}
                      spacing={2}
                      className={classes && classes.container}
                    >
                      <Grid
                        item={true}
                        xs={1}
                        className={classes && classes.tile}
                      >
                        <IconButton
                          aria-label={`info about `}
                          className={classes && classes.icon}
                        >
                          <img src={ElectricityIcon} /> &nbsp;
                          {numberFormat(
                            customerGroupResult.meta.electricityCount,
                            false
                          )}
                        </IconButton>
                        <br />
                        <br />
                        <br />
                        Anzahl
                        <br />
                        Stromverträge
                      </Grid>
                      <Grid
                        item={true}
                        xs={1}
                        className={classes && classes.tile}
                      >
                        <IconButton
                          aria-label={`info about `}
                          className={classes && classes.icon}
                        >
                          <img src={GasIcon} /> &nbsp;
                          {numberFormat(
                            customerGroupResult.meta.gasCount,
                            false
                          )}
                        </IconButton>
                        <br />
                        <br />
                        <br />
                        Anzahl
                        <br />
                        Gasverträge
                      </Grid>
                      <Grid
                        item={true}
                        xs={1}
                        className={classes && classes.tile}
                      >
                        <IconButton
                          aria-label={`info about `}
                          className={classes && classes.icon}
                        >
                          <img src={HeatingIcon} /> &nbsp;
                          {numberFormat(
                            customerGroupResult.meta.heatingCount,
                            false
                          )}
                        </IconButton>
                        <br />
                        <br />
                        <br />
                        Anzahl
                        <br />
                        Fernwärmeverträge
                      </Grid>
                      {isFeatureEnabled(
                        process.env.REACT_APP_FEATURE_GLASS_FIBER
                      ) ? (
                        <Grid
                          item={true}
                          xs={1}
                          className={classes && classes.tile}
                        >
                          <IconButton
                            aria-label={`info about `}
                            className={classes && classes.icon}
                          >
                            <img src={GlassFiberIcon} /> &nbsp;
                            {numberFormat(
                              customerGroupResult.meta.glassFiberCount,
                              false
                            )}
                          </IconButton>
                          <br />
                          <br />
                          <br />
                          Anzahl
                          <br />
                          Glasfaserverträge
                        </Grid>
                      ) : (
                        <></>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </ResponsiveContainer>
            </Grid>
            <Grid item={true} xs={12} lg={6}>
              <Typography variant="h6" align="center">
                {ageHeadline()}
              </Typography>
              <PieChart
                data={ageGroupArray}
                loading={false}
                colorScheme={COLORS}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={optInAndServiceAccordionExpanded}
        onChange={handleOptInAndServiceAccordionChange}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Anteil Kunden ...</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container={true} justify="space-evenly">
            <Grid item={true} xs={12} md={6}>
              <div className={classes && classes.progressbarWrapper}>
                <CircularProgressbar
                  progressbarLabel="mit min. einem OptIn"
                  progressbarValue={getAmountOptIn()}
                />
              </div>
            </Grid>
            <Grid item={true} xs={12} md={6}>
              <div className={classes && classes.progressbarWrapper}>
                <CircularProgressbar
                  progressbarLabel="in min. einer Grundversorgung"
                  progressbarValue={getAmountBasicSupply()}
                />
              </div>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={nextBestActivityAccordionExpanded}
        onChange={handleNextBestActivityAccordionChange}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Verteilung Beratungsfokus Top 10</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container={true}>
            <Grid item={true} xs={12}>
              <div id="nextBestActivityBarChart" />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={clvChurnAccordionExpanded}
        onChange={handleClvChurnAccordionChange}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Durchschnitt CLV / Churn Score</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container={true}>
            <Grid item={true} xs={12}>
              <div id="clvHistogram" />
            </Grid>
            <Grid item={true} xs={12}>
              <div id="churnHistogram" />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </ProgressOverlay>
  );
};

export default CustomerGroupResult;



// WEBPACK FOOTER //
// ./src/components/CustomerGroups/CustomerGroupResult.tsx